import * as React from 'react';
import { useState } from 'react';
import { List, ListItemText,
    Box, Typography,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Divider,
    ListItem,
    ListItemButton,
    ListItemSecondaryAction,
    IconButton,
    } from '@mui/material';
import { Client, Gender, Patient } from '../../models/core';
import { DateTime } from 'luxon';
import SpinnerButton from '../SpinnerButton';
import { GridFormItem } from '../GridForm';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { CheckBoxSharp } from '@mui/icons-material';
import { API as ZiphyAPI, Filter, ListPatientsResult } from '../../utils/ZiphyAPI';
import { API } from '../../utils/Api'
import { Patient as ZiphyPatient, Date as ZiphyDate, Insurance, Place } from "../../models/ziphy";
import { Insurance as OutreachInsurance } from '../../models/insurance';
import useSnackbar from '../../hooks/useSnackbar';
import AlertSnack from '../ErrorSnack';
import useDialog from '../../hooks/useDialog';
import { useInsuranceQuery } from '../../queries/useInsuranceQuery';
import { convertOutreachtoZMRInsurance } from '../../utils/convertUtils';
import { copyAction, ErrorListDialog, useErrorQueue, UseErrorQueueResult } from '../ErrorListDialog';
import ResizableDialog from '../dialogs/ResizableDialog';
import ZiphyPatientConfirmDialog from './ZiphyPatientConfirmDialog';


interface PatientListItemProps {
    patient: ZiphyPatient;
    isSelected: boolean;
    onClick?: (patient: ZiphyPatient) => void;
}

const PatientItem = ({ patient, isSelected, onClick, ...props }: PatientListItemProps): JSX.Element => {
    return (
        <ListItem {...props} >
            <ListItemButton disableRipple onClick={() => { onClick && onClick(patient)}} selected={isSelected}>
                <ListItemText
                    primary={<Typography component='div' sx={{ display: 'flex', justifyContent: 'start' }}>
                        {patient.last_name}, {patient.first_name} ({
                            patient.dob && (`${patient.dob.month}-${patient.dob.day}-${patient.dob.year}`) || 'No DoB'})
                    </Typography>}
                    secondary={
                    <span>
                        Patient ID: {patient?.id || 'unknown'}
                        <br/>
                        Practice Id: {patient.practice_ids || 'unknown'}
                    </span>} />
                <ListItemSecondaryAction>
                    { isSelected ? <IconButton><CheckBoxSharp/></IconButton> : <></> }
                </ListItemSecondaryAction>
            </ListItemButton>
        </ListItem>
    );
};


interface PatientSearchViewProps {
    open: boolean;
    client: Client;
    patient: Patient;
    onUpdate:(zcmId:string, updatedPatient:Patient) => void;
    onClose:() => void;
    onCreatePatient?:() => void;
}

const str2date = (dateStr: string):ZiphyDate => {
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return { _type: 'date', year: date.getFullYear(), month: date.getMonth() + 1, day: date.getDate() };
}

const ZiphyPatientSearchDialog = ({open, client, patient, onUpdate, onClose, onCreatePatient}: PatientSearchViewProps): JSX.Element => {

    const [firstName, setFirstName] = useState<string>(patient?.firstName || '');
    const [lastName, setLastname] = useState<string>(patient?.lastName || '');
    const [dob, setDob] = useState<DateTime|undefined>(DateTime.fromISO(patient?.dob || ''));
    const [zcmId, setZCMID] = useState<string>(patient?.zcmId || '');
    
    const [searching, setSearching] = useState<boolean>(false);
    const [selectedPatientId, setSelectedPatientId] = useState<string>();
    const [filteredPatients, setFilteredPatients] = useState<ZiphyPatient[]|undefined>();
    
    const snackBar = useSnackbar();
    const [snackBarAlert, setSnackBarAlert] = useState<string>();
    const { show:showComDiag, hide:hideComDiag, open:comDiagIsOpen } = useDialog();

    const insuranceQuery = useInsuranceQuery(client._id, patient._id);
    const errorQueue = useErrorQueue();

    React.useEffect(() => {
        if(snackBarAlert) {
            snackBar.showSuccess(snackBarAlert);
            setSnackBarAlert(undefined);
        }
    },[snackBarAlert, snackBar])

    const requestFilteredPatients = React.useCallback(async (errorQueue:UseErrorQueueResult) => {
        const dobPlus1 = dob?.plus({days:1});
        const dobMinus1 = dob?.plus({days:-1});
        const filter:Filter = (zcmId?.trim()) ? {
            and: [
                { eq: ['is_hidden', false] },
                { eq: ["id", Number.parseInt(zcmId)] },
                { contains: ["practice_ids", [client.practiceId]] }
            ]
        } : {
            and: [
                { eq: ['is_hidden', false] },
                {
                    and:[
                        (firstName?.trim() ? { imatch: ['first_name', firstName] } : {}),
                        (lastName?.trim() ? { imatch: ['last_name', lastName] } : {}),
                        (dob ?
                            {
                                or:[
                                    { eq: ['dob', { _type: 'date', year: dob.year, month: dob.month, day: dob.day }] },
                                    dobPlus1 ? { eq: ['dob', { _type: 'date', year: dobPlus1.year, month: dobPlus1.month, day: dobPlus1.day }] } : {},
                                    dobMinus1 ? { eq: ['dob', { _type: 'date', year: dobMinus1.year, month: dobMinus1.month, day: dobMinus1.day }] } : {}
                                ] 
                            }
                            : {}
                        )
                    ]
                },
                { contains: ["practice_ids", [client.practiceId]] }
            ]
        }
            
        try {
            const request:ListPatientsResult = await ZiphyAPI.getPatients(filter);
            setFilteredPatients(request?.patients?.items || []);
        }catch(error) {
            console.error(error);
            errorQueue.enqueue({message:'Failed to retrieve Users List.'});
        }
        setSearching(false);

    }, [client, firstName, lastName, zcmId, dob])

    const patientListComponant = React.useMemo(() => {
        return <List>
            {
                (filteredPatients || []) .map((m, i) => {
                    const targetId = m.id?.toString();
                    const isSelected = selectedPatientId != undefined && targetId != undefined && selectedPatientId == targetId;
                    return <PatientItem  key={'pmid-'+i} patient={m} 
                        isSelected={isSelected}
                        onClick={() => setSelectedPatientId(isSelected ? undefined : targetId?.toString()) }/>
                })
            }
        </List>
    }, [filteredPatients, selectedPatientId]);

    const updatePatientID = React.useCallback(async (patient:Patient, newZCMID:string,
        errorQueue:UseErrorQueueResult,
        onUpdate:(zcmId:string, updatedPatient:Patient) => void
    ) => {
        const updatedPatient = { ...patient, ...{ zcmId:newZCMID }};
        try {
            const request = await API.updatePatient(updatedPatient);
            if(!request) {
                throw Error(`Request to update patient: ${patient._id} with id ${newZCMID}`);
            }
            onUpdate && onUpdate(newZCMID, updatedPatient);
            setZCMID(newZCMID);
            setSnackBarAlert(`Successfully updated ${updatedPatient.lastName},${updatedPatient.firstName} ID to ${newZCMID}`);

            await requestFilteredPatients(errorQueue);
        }catch(error) {
            console.log(error);
            errorQueue.enqueue({
                title: "Update Patient Error",
                message:(error as Error)?.message || `Failed to update patient: ${patient._id}`,
                type:'error'
            });
            errorQueue.setTitle(<b>Error</b>);
            errorQueue.setBody('Failed to update the patient with new Ziphy Core ID.');
        }
    },[requestFilteredPatients]);

    const confirmLinkPatientBox = React.useCallback((linkingPatient:Patient, client:Client,
        insuranceList:OutreachInsurance[], onCancel:()=>void,
        onConfirm:(patient:Patient, insurance:OutreachInsurance[], place:Place|null)=>void) => {
        return <ZiphyPatientConfirmDialog open={true} patient={linkingPatient} client={client} insurance={insuranceList} 
            onConfirm={ async (pat,ins,plc) => await onConfirm(pat, ins, plc) } onClose={onCancel}/>
    },[]);

    const createZiphyPatient = React.useCallback(async (patient:Patient, practiceId:number,
        insurances:OutreachInsurance[], place:Place|null, errorQueue:UseErrorQueueResult,
        onUpdate:(zcmId:string, updatedPatient:Patient) => void
    ) => {
        const primePhoneNumber = patient.phone?.startsWith('+1')
            ? patient.phone
            : patient.phone?.startsWith('1')
                ? ('+' + patient.phone)
                : patient.phone
                    ? '+1' + patient.phone
                    : undefined;

        const altPhoneNumber = patient.phone2?.startsWith('+1')
            ? patient.phone
            : patient.phone?.startsWith('1')
                ? ('+' + patient.phone)
                : patient.phone
                    ? '+1' + patient.phone
                    : undefined;
        
        try {
            const isProduction = process.env.NODE_ENV === 'production'
            const patientGender = patient.gender?.toLowerCase();
            const phone = altPhoneNumber?.replaceAll(/[^0-9+]/g, '');
            const request = await ZiphyAPI.createPatient(
                { dob:str2date(patient.dob || ''), code:'', 
                    first_name:patient.firstName, last_name:patient.lastName,
                    phone:phone && phone != '' ? phone : undefined,
                    email:patient.email && patient.email != '' ? patient.email : undefined,
                    gender:patientGender != Gender.UNKNOWN.toLocaleLowerCase() ? patientGender : 'na',
            }, practiceId);
            if(!request) {
                const err = !isProduction ? (request as Error).message : undefined;
                errorQueue.setTitle(<b>Error</b>);
                errorQueue.setBody('Failed to create new patient in Ziphy Admin.');
                throw Error(`Failed to create ziphy patient: ${patient.lastName}, ${patient.firstName} ${err ? '\n' + err : ''}`);
            }

            const newZCMID = request.id?.toString() || '';
            const numZCMID = Number.parseInt(newZCMID);

            try {
                const prime = primePhoneNumber?.replaceAll(/[^0-9+]/g, '');
                if(prime && prime.length <= 7) {
                    const upReq = await ZiphyAPI.createContact(numZCMID, {
                        system:'phone',
                        use:'home',
                        value:prime,
                    });
                    if(!upReq) {
                        const err = !isProduction ? (upReq as Error).message : undefined;
                        throw Error(`Failed to create patient contact details: ${patient.lastName}, ${patient.firstName} ${err ? '\n' + err : ''}`);
                    }
                }
            }catch(error) {
                console.warn(error);
                errorQueue.enqueue({message:`Failed to Add Phone Number: ${patient.phone}.\nPlease add in Ziphy Admin`, type:'warning'});
            }

            const insuranceList = insurances ||[];
            for(const insurance of insuranceList) {
                try {
                    const zmrIns = convertOutreachtoZMRInsurance(insurance);
                    await ZiphyAPI.createInsurance({...zmrIns, patient_id:numZCMID} as Insurance).catch((e) => {
                        console.error(e);
                        errorQueue.enqueue({message:`Failed to Add Insurance: ${insurance.providerName}.\nPlease add in Ziphy Admin`, type:'warning' });
                    });
                } catch(error) {
                    console.error(error);
                    errorQueue.enqueue({message:`Failed to Add Insurance: ${insurance.providerName}.\nPlease add in Ziphy Admin`, type:'warning' });
                }
            }

            if(place) {
                try {
                    let workingPlace = place;
                    if(!workingPlace.id || workingPlace.id == 0) {
                        workingPlace = await ZiphyAPI.createPlace(place, practiceId)
                        if(!workingPlace) {
                            errorQueue.enqueue({message:`Failed to Create Place: ${place.building} ${place.street}.\nPlease add in Ziphy Admin`, type:'warning' });
                        }
                    }
                    if(!workingPlace.id || workingPlace.id == 0) {
                        const reqUp = await ZiphyAPI.updatePatient({id:numZCMID, place_id:workingPlace.id})
                        if(!reqUp) {
                            errorQueue.enqueue({message:`Failed to Add Place to Patient: ${place.building} ${place.street}.\nPlease add in Ziphy Admin`, type:'warning' });
                        }
                    }
                } catch(error) {
                    console.error(error);
                    errorQueue.enqueue({message:`Failed to Add Place: ${place.building} ${place.street}.\nPlease add in Ziphy Admin`, type:'warning' });
                }
            }

            try {
                const lines = [ patient.address || '', patient.address2 || '' ].filter(e => e && e != '');
                const upReq = await ZiphyAPI.createAddresses(numZCMID, {
                    city: patient.city,
                    country: 'United States',
                    state: patient.state,
                    line: lines,
                    postalCode: patient.zip,
                    use:'home',
                    type:'physical'
                });
                if(!upReq) {
                    throw Error('Failed to create address');
                }
            }catch(error) {
                console.warn(error);
                errorQueue.enqueue({message:`Failed to Create Address: ${patient.address}.\nPlease add in Ziphy Admin`, type:'warning' });
            }

            const updatePatient = {...patient, ...{zcmId:newZCMID}};
            const requestUpdate = await API.updatePatient(updatePatient);
            if(!requestUpdate) {
                throw Error(`Failed to update patient: ${patient._id}`);
            }
            onUpdate && onUpdate(newZCMID, updatePatient);
            onCreatePatient && onCreatePatient();

            setZCMID(newZCMID);
            setSnackBarAlert(`Successfully Create Ziphy Patient ${updatePatient.lastName},${updatePatient.firstName} ID to ${newZCMID}`);

        } catch(error) {
            console.log(error);
            errorQueue.enqueue({message:(error as Error)?.message || `Failed to create patient: ${patient.lastName},${patient.firstName}`, type:'error' });
            
        }
    }, [onCreatePatient]);

    return (
        <>
        <ResizableDialog
            open={open} title={'Patient Search'} canExpand={true} onClose={onClose}
            initialSettings={ { sx: { height:'60%', maxHeight:'60%', width: '50%', maxWidth: '60%', minWidth:'300px' } }}
        >
            <DialogContent sx={{ display: 'flex', flexDirection: 'row' }}>
                <Box sx={{ width: '25%', minWidth:'150px', borderRight: '1px solid #ccc', padding: '4px', paddingRight:'15px', paddingBottom:'8px' }}>
                    <GridFormItem xs={12}>
                        <GridFormItem xs={12}>
                            <TextField name='zcmId' label='ZCM Patient ID' value={zcmId}
                                onChange={(val) => setZCMID(val.target.value) } fullWidth
                                variant='standard' />
                        </GridFormItem>
                        <Divider/>
                        <GridFormItem xs={12}>
                            <TextField name='firstName' label='First Name' value={firstName}
                                onChange={(ev) => setFirstName(ev.target.value || '')} fullWidth
                                variant='standard' />
                        </GridFormItem>
                        <Divider/>
                        <GridFormItem xs={12}>
                            <TextField name='lastName' label='Last Name' value={lastName}
                                onChange={(ev) => setLastname(ev.target.value || '')} fullWidth
                                variant='standard' />
                        </GridFormItem>
                        <Divider/>
                        <GridFormItem xs={12}>
                            <LocalizationProvider dateAdapter={AdapterLuxon}>
                                <DatePicker label='Date of Birth' inputFormat='MM/dd/yyyy'
                                    value={ dob }
                                    renderInput={
                                        (params) => <TextField name='dob' required={false} {...params} fullWidth
                                        variant='standard' helperText='Format: MM/DD/YYYY' error={false}/>
                                    }
                                    onChange={ (e) => setDob(e||undefined) } />
                            </LocalizationProvider>
                        </GridFormItem>
                        <Divider/>
                        <GridFormItem xs={12}><Box sx={{ height: '1em' }} /></GridFormItem>
                        <GridFormItem xs={12}>
                            <SpinnerButton onClick={async ()  => await requestFilteredPatients(errorQueue) }
                                showSpinner={searching}>Search For Patients</SpinnerButton>
                        </GridFormItem>
                    </GridFormItem>
                </Box>
                <Box sx={{ width: '75%', padding: '16px' }}>
                    {
                        (filteredPatients && filteredPatients.length > 0) ? patientListComponant :
                        <Box display="flex" justifyContent="center" alignItems="center" height='80%'>
                            <Box width={'60%'} height={'60%'} display="flex" justifyContent="center" alignItems="center"
                                minWidth={200} minHeight={200}
                                border={1} borderRadius={1} boxShadow={1} p={2}>
                                <Typography variant="h6" align="center">
                                    {'No Patients Found.'}
                                    <br/>
                                    {'Set search filters and press search to proceed.'}
                                </Typography>
                            </Box>
                        </Box>
                    }
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
                <Button onClick={() => selectedPatientId && updatePatientID(patient, selectedPatientId, errorQueue, onUpdate) }
                    disabled={(selectedPatientId == undefined || selectedPatientId == '')}>
                    Link Patient
                </Button>
                { onCreatePatient && <Button disabled={ (patient.zcmId && patient.zcmId != '') || onCreatePatient == null} onClick={showComDiag}>New Patient</Button> }
            </DialogActions>
            { ((!patient.zcmId || patient.zcmId == '') && comDiagIsOpen)
                && confirmLinkPatientBox(patient, client, insuranceQuery.data||[], hideComDiag,
                    async (pt, ins, plc) => {
                        await createZiphyPatient(pt, client.practiceId, ins, plc, errorQueue, onUpdate);
                        hideComDiag();
                    }
            )}
            <AlertSnack {...snackBar}/>
        </ResizableDialog>
         <ErrorListDialog
            open={errorQueue.dialogOpen}
            onClose={errorQueue.clear}
            title={errorQueue.title || <b>Attention</b>}
            description={errorQueue.body || <b>The following issues have occured</b>}
            errors={errorQueue.errors}
            globalActions={[copyAction]}
        />
        </>
    );
}

export default ZiphyPatientSearchDialog;
